import { createFeatureSelector, createSelector } from '@ngrx/store';
import { TaskFilter, tasksFeatureKey, TasksState } from './tasks.reducer';
import {
  ImplementationTimelineEnum,
  ImplementationTimelineFieldNames,
  OwnershipFieldNames,
  ReferenceTypeEnum,
  SeverityEnum,
  SeverityFieldNames,
  Task,
  TaskAssignedRoles,
  TaskDetails,
  StakeholderRoles,
  TaskStatusEnum,
  TASK_STATUS_COLORS,
  TaskDetailsStakeholders,
  ActionBox,
} from '../../../core/interfaces';

export const getTasksState = createFeatureSelector<TasksState>(tasksFeatureKey);

export interface TaskStatusChartItem {
  id: TaskStatusEnum;
  name: string;
  percentage: number;
  color: string;
  tooltipText: string;
  count: number;
}

export const getFilters = createSelector(
  getTasksState,
  ({ filters }) => filters ?? [],
);

export const getSeverityData = createSelector(getTasksState, ({ tasks }) => {
  const items = {
    [SeverityFieldNames.CRITICAL]:
      tasks?.filter((task) => task.severity === 3).length || 0,
    [SeverityFieldNames.HIGH]:
      tasks?.filter((task) => task.severity === 2).length || 0,
    [SeverityFieldNames.MEDIUM]:
      tasks?.filter((task) => task.severity === 1).length || 0,
    [SeverityFieldNames.LOW]:
      tasks?.filter((task) => task.severity === 0).length || 0,
  };
  return {
    items,
    isLarge: Object.values(items).some((item) => item > 9),
  };
});

export const getTimelineData = createSelector(getTasksState, ({ tasks }) => {
  const items = {
    [ImplementationTimelineFieldNames.OVERDUE]:
      tasks?.filter(
        (task) =>
          task.implementationTimeline === ImplementationTimelineEnum.overdue,
      ).length || 0,
    [ImplementationTimelineFieldNames.IMMEDIATE]:
      tasks?.filter(
        (task) =>
          task.implementationTimeline === ImplementationTimelineEnum.immediate,
      ).length || 0,
    [ImplementationTimelineFieldNames.TWO_WEEKS]:
      tasks?.filter(
        (task) =>
          task.implementationTimeline === ImplementationTimelineEnum.twoWeeks,
      ).length || 0,
    [ImplementationTimelineFieldNames.NO_RUSH]:
      tasks?.filter(
        (task) =>
          task.implementationTimeline === ImplementationTimelineEnum.noRush,
      ).length || 0,
  };
  return {
    items,
    isLarge: Object.values(items).some((item) => item > 9),
  };
});

export const getOwnershipData = createSelector(getTasksState, ({ tasks }) => {
  const items = {
    [OwnershipFieldNames.OWNER]:
      tasks?.reduce(
        (count, task) =>
          count +
          (task.raci.users.owner?.some((owner) => owner?.isCurrentUser)
            ? 1
            : 0),
        0,
      ) || 0,
    [OwnershipFieldNames.ACCOUNTABLES]:
      tasks?.reduce(
        (count, task) =>
          count +
          (task.raci.users.accountables?.some(
            (accountable) => accountable.isCurrentUser,
          )
            ? 1
            : 0),
        0,
      ) || 0,
    [OwnershipFieldNames.CONSULTEDS]:
      tasks?.reduce(
        (count, task) =>
          count +
          (task.raci.users.consulteds?.some(
            (consulted) => consulted.isCurrentUser,
          )
            ? 1
            : 0),
        0,
      ) || 0,
    [OwnershipFieldNames.INFORMEDS]:
      tasks?.reduce(
        (count, task) =>
          count +
          (task.raci.users.informeds?.some((informed) => informed.isCurrentUser)
            ? 1
            : 0),
        0,
      ) || 0,
  };
  return {
    items,
    isLarge: Object.values(items).some((item) => item > 9),
  };
});

export const getAssignedRoles = createSelector(
  getTasksState,
  ({ tasks = [], departmentsColors = {} }) => {
    const departmentItems = tasks.flatMap((task) => task.allDepartments ?? []);

    const idCount = new Map<
      string,
      { count: number; id: string; name: string }
    >();
    departmentItems.forEach((item) => {
      if (!item?.id) {
        return;
      }
      const key = String(item.id);
      const existing = idCount.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        idCount.set(key, { count: 1, id: item.id, name: item.name });
      }
    });

    const totalItems = departmentItems.length;
    return Array.from(idCount.values()).map(({ id, name, count }) => ({
      id,
      name,
      tooltipText: `${count}`,
      percentage: totalItems ? (count / totalItems) * 100 : 0,
      color: departmentsColors[String(id)],
    }));
  },
);

export const getTaskStatusDistribution = createSelector(
  getTasksState,
  ({ tasks = [], taskStatuses }) => {
    if (!taskStatuses?.length) {
      return [] as TaskStatusChartItem[];
    }

    const totalTasks = tasks.length || 0;

    return taskStatuses
      .map((status) => {
        const count = tasks.filter(
          (task) => task.taskStatusId === status.id,
        ).length;
        const percentage = totalTasks
          ? Number(((count / totalTasks) * 100).toFixed(2))
          : 0;

        return {
          id: status.id,
          name: status.name,
          percentage,
          color: TASK_STATUS_COLORS[status.id],
          tooltipText: `${count}`,
          count,
        };
      })
      .filter((item) => item.count > 0);
  },
);

export const getTasks = createSelector(
  getTasksState,
  ({ tasks }) => tasks ?? [],
);

export const getIsLoading = createSelector(
  getTasksState,
  ({ isLoading, taskStatusesLoading }) =>
    (isLoading !== false || taskStatusesLoading !== false) ?? true,
);

export const tasksIsLoading = createSelector(
  getTasksState,
  ({ isLoading }) => isLoading ?? true,
);

export const getDetailsLoading = createSelector(
  getTasksState,
  ({ detailsLoading }) => detailsLoading ?? true,
);

export const getShowSidebar = createSelector(
  getTasksState,
  ({ showSidebar }) => showSidebar ?? false,
);

export const getSelectedTaskId = createSelector(
  getTasksState,
  ({ selectedTaskId }) => selectedTaskId,
);

export const getDepartmentsColors = createSelector(
  getTasksState,
  ({ departmentsColors }) => departmentsColors ?? {},
);

export const getTaskFilters = createSelector(
  getTasksState,
  ({ taskFilters }) => taskFilters,
);

export const selectFilteredTasks = createSelector(
  getTasksState,
  ({ tasks = [], taskFilters }) => {
    return filterTasks(tasks, taskFilters);
  },
);

export const isTasksEmpty = createSelector(
  selectFilteredTasks,
  getTasksState,
  (tasks, { isLoading }) => tasks?.length === 0 && !isLoading,
);

export const selectNewTasks = createSelector(selectFilteredTasks, (tasks) => {
  return tasks.filter((task) => !task.isReviewed);
});

export const selectReviewedTasks = createSelector(
  selectFilteredTasks,
  (tasks) => {
    return tasks.filter((task) => task.isReviewed);
  },
);

export const selectTasksStakeholders = (state: TasksState) =>
  state.taskDetails?.tasksStakeholders;

export const selectStakeholderInfo = createSelector(
  getTasksState,
  ({ taskDetails }) => {
    if (!taskDetails?.tasksStakeholders) {
      return { isCurrentUserInStakeholders: false, stakeholdersCount: 0 };
    }

    const stakeholderGroups = [
      StakeholderRoles.OWNER,
      StakeholderRoles.ACCOUNTABLE,
      StakeholderRoles.CONSULTEDS,
      StakeholderRoles.INFORMDES,
    ];

    let isCurrentUserInStakeholders = false;
    let stakeholdersCount = 0;
    stakeholderGroups.forEach((group) => {
      const groupList =
        taskDetails?.tasksStakeholders[group as StakeholderRoles];
      if (groupList && Array.isArray(groupList)) {
        stakeholdersCount += groupList.filter(
          (user) => !user.isCurrentUser,
        ).length;

        if (groupList.some((user) => user.isCurrentUser)) {
          isCurrentUserInStakeholders = true;
        }
      } else if (groupList) {
        stakeholdersCount++;
        if ((groupList as any)?.isCurrentUser) {
          isCurrentUserInStakeholders = true;
          stakeholdersCount--;
        }
      }
    });

    return { isCurrentUserInStakeholders, stakeholdersCount };
  },
);

export const getTaskDetails = createSelector(
  getTasksState,
  ({ taskDetails, tasks, selectedTaskId }) => {
    const flattenedArray: any[] = [];
    Object.entries(taskDetails?.tasksStakeholders || {}).forEach(
      ([role, people]) => {
        let roleName = '';
        switch (role) {
          case StakeholderRoles.OWNER:
            roleName = 'owner';
            break;
          case StakeholderRoles.ACCOUNTABLE:
            roleName = 'accountable';
            break;
          case StakeholderRoles.INFORMDES:
            roleName = 'informed';
            break;
          case StakeholderRoles.CONSULTEDS:
            roleName = 'consultant';
            break;
        }
        if (Array.isArray(people)) {
          people.forEach((person) => {
            flattenedArray.push({
              id: person.userId,
              name: person.userName,
              depName: person.depName,
              depId: person.depId,
              roleName,
              role,
              isCurrentUser: person.isCurrentUser,
            });
          });
        } else {
          flattenedArray.push({
            id: people.userId,
            name: people.userName,
            depName: people.depName,
            depId: people.depId,
            roleName,
            role,
            isCurrentUser: people.isCurrentUser,
          });
        }
      },
    );

    // Step 2: Sort the array based on the specified order
    const sortedArray = flattenedArray.sort((a, b) => {
      // Sort by role first: 'owner' should be first, followed by 'accountables', 'consulteds', and 'informeds'
      const roleOrder = ['owner', 'accountables', 'consulteds', 'informeds'];
      const aRoleIndex = roleOrder.indexOf(a.role);
      const bRoleIndex = roleOrder.indexOf(b.role);

      if (aRoleIndex !== bRoleIndex) {
        return aRoleIndex - bRoleIndex;
      }

      // If roles are the same, check if the person is the current user
      if (a.isCurrentUser) return -1;
      if (b.isCurrentUser) return 1;

      return 0;
    });
    const selectedTask = tasks?.find((task) => task.id === selectedTaskId);
    const normalizedTaskDetails = taskDetails
      ? {
          ...taskDetails,
          isBlockedByPrerequisites:
            taskDetails.isBlockedByPrerequisites ?? false,
          blockingPrerequisiteTasks:
            taskDetails.blockingPrerequisiteTasks ?? [],
        }
      : undefined;

    if (!normalizedTaskDetails && selectedTask) {
      return {
        ...selectedTask,
        actionBox: {} as ActionBox,
        taskId: selectedTask.id,
        taskName: selectedTask.name,
        taskStatus: selectedTask.taskStatusId,
        taskReferences: [],
        comments: [],
        taskDetailsCommentPending: false,
        taskDetailsCommentFailed: false,
        newComment: undefined,
        tasksStakeholders: {} as TaskDetailsStakeholders,
        stakeholders: [],
        remediationSteps: '',
        taskDescription: '',
        totalSimilarTasksInstatusClose: 0,
        totalSimilarTasks: 0,
        blockingPrerequisiteTasks: [],
      };
    }
    return {
      ...selectedTask,
      ...normalizedTaskDetails,
      taskReferences: [...(normalizedTaskDetails?.taskReferences || [])].sort(
        (a, b) => {
          if (a.referenceType === ReferenceTypeEnum.RISK) {
            return -1;
          } else if (b.referenceType === ReferenceTypeEnum.RISK) {
            return 1;
          } else {
            return 0;
          }
        },
      ),
      tasksStakeholders: normalizedTaskDetails?.tasksStakeholders ?? {},
      comments: normalizedTaskDetails?.comments,
      isBlockedByPrerequisites:
        normalizedTaskDetails?.isBlockedByPrerequisites ?? false,
      blockingPrerequisiteTasks:
        normalizedTaskDetails?.blockingPrerequisiteTasks ?? [],
      stakeholders: sortedArray as any,
    } as TaskDetails & Task;
  },
);

function matchesAssignedRoleFilter(
  task: Task,
  assignedRoleIds: string[],
): boolean {
  return (task.allDepartments ?? []).some((department) =>
    assignedRoleIds.some((roleId) => String(roleId) === String(department.id)),
  );
}

function filterTasks(tasks: Task[], taskFilters: TaskFilter): Task[] {
  return tasks.filter((task) => {
    // Severity Filter (OR logic within the same filter)
    const matchesSeverity =
      taskFilters.severity?.length === 0 ||
      taskFilters.severity?.includes(task.severity);

    // Timeline Filter (OR logic within the same filter)
    const matchesTimeline =
      taskFilters.implementationTimeline?.length === 0 ||
      taskFilters.implementationTimeline?.includes(task.implementationTimeline);

    // Ownership Filter (OR logic within the same filter)
    const matchesOwnership =
      taskFilters.ownership?.length === 0 ||
      taskFilters.ownership?.some((role) => {
        return task.raci.users[role as keyof TaskAssignedRoles]?.some(
          (user) => user.isCurrentUser,
        );
      });

    // Domain Filter (OR logic within the same filter)
    const matchesDomain =
      taskFilters.domains?.length === 0 ||
      task.domains.some((d) => taskFilters.domains?.includes(d.domainId));

    // Assigned Roles Filter (OR logic within the same filter)
    const matchesAssignedRoles =
      taskFilters.assignedRoles?.length === 0 ||
      matchesAssignedRoleFilter(task, taskFilters.assignedRoles);

    const matchesStatuses =
      taskFilters.statuses?.length === 0 ||
      taskFilters.statuses?.includes(task.taskStatusId);

    // Combine all filters using AND logic across different categories
    return (
      matchesSeverity &&
      matchesTimeline &&
      matchesOwnership &&
      matchesDomain &&
      matchesAssignedRoles &&
      matchesStatuses
    );
  });
}

export const getAvailabilityOfFilters = createSelector(
  getTasksState,
  ({ taskFilters, tasks = [], taskStatuses = [] }) => {
    // Create filter variants
    const filtersWithoutSeverity: TaskFilter = { ...taskFilters, severity: [] };
    const filtersWithoutTimeline: TaskFilter = {
      ...taskFilters,
      implementationTimeline: [],
    };
    const filtersWithoutOwnership: TaskFilter = {
      ...taskFilters,
      ownership: [],
    };
    const filtersWithoutDomain: TaskFilter = { ...taskFilters, domains: [] };
    const filtersWithoutAssignedRoles: TaskFilter = {
      ...taskFilters,
      assignedRoles: [],
    };
    const filtersWithoutStatuses: TaskFilter = { ...taskFilters, statuses: [] };

    // Apply filters once and store results for reuse
    const tasksWithoutSeverityFilter = filterTasks(
      tasks,
      filtersWithoutSeverity,
    );
    const tasksWithoutTimelineFilter = filterTasks(
      tasks,
      filtersWithoutTimeline,
    );
    const tasksWithoutOwnershipFilter = filterTasks(
      tasks,
      filtersWithoutOwnership,
    );
    const tasksWithoutDomainFilter = filterTasks(tasks, filtersWithoutDomain);
    const tasksWithoutAssignedRolesFilter = filterTasks(
      tasks,
      filtersWithoutAssignedRoles,
    );
    const tasksWithoutStatusesFilter = filterTasks(
      tasks,
      filtersWithoutStatuses,
    );

    const departmentsMap = new Map<string, { id: string; name: string }>();
    tasksWithoutAssignedRolesFilter.forEach((task) => {
      (task.allDepartments ?? []).forEach((department) => {
        const key = String(department.id);
        if (department?.id && !departmentsMap.has(key)) {
          departmentsMap.set(key, department);
        }
      });
    });
    const uniqueDepartments = Array.from(departmentsMap.values());

    const assignedRolesAvailability = uniqueDepartments.reduce(
      (availability, role) => {
        const roleId = String(role.id);
        availability[roleId] =
          tasksWithoutAssignedRolesFilter.some((task) =>
            matchesAssignedRoleFilter(task, [roleId]),
          ) ||
          taskFilters.assignedRoles.some(
            (filterId) => String(filterId) === roleId,
          );
        return availability;
      },
      {} as { [key: string]: boolean },
    );

    // Process domains data once
    const allExistingDomains = tasks
      .flatMap((item) => item.domains)
      .filter(
        (domain, index, self) =>
          index === self.findIndex((d) => d.domainId === domain.domainId),
      );

    // Calculate domains availability
    const domainsAvailability = allExistingDomains.reduce(
      (availability, domain) => {
        availability[domain.domainId] =
          tasksWithoutDomainFilter.some((task) =>
            task.domains.some(
              (taskDomain) => taskDomain.domainId === domain.domainId,
            ),
          ) || taskFilters.domains.includes(domain.domainId);
        return availability;
      },
      {} as { [key: string]: boolean },
    );

    const statusesAvailability = taskStatuses.reduce(
      (availability, status) => {
        availability[status.id] =
          tasksWithoutStatusesFilter.some(
            (task) => task.taskStatusId === status.id,
          ) || taskFilters.statuses.includes(status.id);
        return availability;
      },
      {} as Partial<Record<TaskStatusEnum, boolean>>,
    );

    return {
      severity: {
        [SeverityEnum.critical]:
          tasksWithoutSeverityFilter.some(
            (task) => task.severity === SeverityEnum.critical,
          ) || taskFilters.severity.includes(SeverityEnum.critical),
        [SeverityEnum.high]:
          tasksWithoutSeverityFilter.some(
            (task) => task.severity === SeverityEnum.high,
          ) || taskFilters.severity.includes(SeverityEnum.high),
        [SeverityEnum.medium]:
          tasksWithoutSeverityFilter.some(
            (task) => task.severity === SeverityEnum.medium,
          ) || taskFilters.severity.includes(SeverityEnum.medium),
        [SeverityEnum.low]:
          tasksWithoutSeverityFilter.some(
            (task) => task.severity === SeverityEnum.low,
          ) || taskFilters.severity.includes(SeverityEnum.low),
      },
      implementationTimeline: {
        [ImplementationTimelineEnum.overdue]:
          tasksWithoutTimelineFilter.some(
            (task) =>
              task.implementationTimeline ===
              ImplementationTimelineEnum.overdue,
          ) ||
          taskFilters.implementationTimeline.includes(
            ImplementationTimelineEnum.overdue,
          ),
        [ImplementationTimelineEnum.immediate]:
          tasksWithoutTimelineFilter.some(
            (task) =>
              task.implementationTimeline ===
              ImplementationTimelineEnum.immediate,
          ) ||
          taskFilters.implementationTimeline.includes(
            ImplementationTimelineEnum.immediate,
          ),
        [ImplementationTimelineEnum.twoWeeks]:
          tasksWithoutTimelineFilter.some(
            (task) =>
              task.implementationTimeline ===
              ImplementationTimelineEnum.twoWeeks,
          ) ||
          taskFilters.implementationTimeline.includes(
            ImplementationTimelineEnum.twoWeeks,
          ),
        [ImplementationTimelineEnum.noRush]:
          tasksWithoutTimelineFilter.some(
            (task) =>
              task.implementationTimeline === ImplementationTimelineEnum.noRush,
          ) ||
          taskFilters.implementationTimeline.includes(
            ImplementationTimelineEnum.noRush,
          ),
      },
      ownership: {
        [OwnershipFieldNames.OWNER]:
          tasksWithoutOwnershipFilter.some((task) =>
            task.raci.users.owner?.some((owner) => owner?.isCurrentUser),
          ) || taskFilters.ownership.includes(OwnershipFieldNames.OWNER),
        [OwnershipFieldNames.ACCOUNTABLES]:
          tasksWithoutOwnershipFilter.some((task) =>
            task.raci.users.accountables?.some(
              (accountable) => accountable.isCurrentUser,
            ),
          ) || taskFilters.ownership.includes(OwnershipFieldNames.ACCOUNTABLES),
        [OwnershipFieldNames.CONSULTEDS]:
          tasksWithoutOwnershipFilter.some((task) =>
            task.raci.users.consulteds?.some(
              (consulted) => consulted.isCurrentUser,
            ),
          ) || taskFilters.ownership.includes(OwnershipFieldNames.CONSULTEDS),
        [OwnershipFieldNames.INFORMEDS]:
          tasksWithoutOwnershipFilter.some((task) =>
            task.raci.users.informeds?.some(
              (informed) => informed.isCurrentUser,
            ),
          ) || taskFilters.ownership.includes(OwnershipFieldNames.INFORMEDS),
      },

      assignedRoles: assignedRolesAvailability,
      domains: domainsAvailability,
      statuses: statusesAvailability,
    };
  },
);

export const getTaskStatuses = createSelector(
  getTasksState,
  ({ taskStatuses }) => taskStatuses,
);
